<template>
  <a-modal
    :title="title"
    width="880px"
    :open="open"
    :bodyStyle="{ maxHeight: '78vh', overflowY: 'auto', padding: '0 24px' }"
    :confirm-loading="loading"
    cancel-text="取消"
    ok-text="确定"
    centered
    destroy-on-close
    @cancel="handleCancel"
    @ok="handleOk"
  >
    <a-form
      ref="formRef"
      :model="model"
      layout="vertical"
      size="small"
    >
      <a-divider orientation="left">基本信息</a-divider>
      <a-row :gutter="16">
        <a-col :span="12">
          <a-form-item
            label="数据源名称"
            name="name"
            :rules="rules.name"
          >
            <a-input
              v-model:value="model.name"
              placeholder="请填写数据源名称"
              allow-clear
            />
          </a-form-item>
        </a-col>
        <a-col :span="12">
          <a-form-item
            label="数据源类型"
            name="source_type"
            :rules="rules.source_type"
          >
            <a-select
              v-model:value="model.source_type"
              placeholder="请选择"
              @change="onSourceTypeChange"
            >
              <a-select-option
                v-for="opt in sourceTypeOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
        <a-col
          v-if="model.source_type === 'db'"
          :span="12"
        >
          <a-form-item
            label="数据库类型"
            name="db_type"
            :rules="rules.db_type"
          >
            <a-select
              v-model:value="model.db_type"
              placeholder="请选择"
            >
              <a-select-option
                v-for="opt in dbTypeOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
      </a-row>

      <!-- 数据库类型：连接信息 -->
      <template v-if="model.source_type === 'db'">
        <a-divider orientation="left">数据库连接信息</a-divider>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              label="数据库主机"
              name="db_host"
              :rules="rules.db_host"
            >
              <a-input
                v-model:value="model.db_host"
                placeholder="如 127.0.0.1"
                allow-clear
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              label="数据库端口"
              name="db_port"
              :rules="rules.db_port"
            >
              <a-input-number
                v-model:value="model.db_port"
                style="width: 100%"
                :min="1"
                :max="65535"
                placeholder="如 3306"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              label="数据库用户"
              name="db_user"
              :rules="rules.db_user"
            >
              <a-input
                v-model:value="model.db_user"
                placeholder="如 root"
                allow-clear
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              label="数据库密码"
              name="db_password"
            >
              <a-input-password
                v-model:value="model.db_password"
                placeholder="修改时留空则不修改密码"
                allow-clear
                autocomplete="new-password"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              :label="db_name_field_lable"
              name="db_name"
              :rules="rules.db_name"
            >
              <a-input
                v-model:value="model.db_name"
                placeholder="如 my_db"
                allow-clear
              />
            </a-form-item>
          </a-col>
          <a-col
            :span="12"
            v-if="model.db_type == 'oracle'"
          >
            <a-form-item label="默认Schema">
              <a-input
                v-model:value="oracle_schema"
                allow-clear
              />
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item
          label="数据库选项"
          name="db_options"
        >
          <JsonStr
            v-model:value="model.db_options"
            placeholder='可选，JSON 对象，如 {"charset":"utf8mb4"}'
          />
        </a-form-item>
      </template>

      <!-- 自定义类型：采集函数 -->
      <template v-else-if="model.source_type === 'custom'">
        <a-divider orientation="left">自定义采集</a-divider>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              label="自定义采集函数名称"
              name="custom_func"
              :rules="rules.custom_func"
            >
              <a-select
                v-model:value="model.custom_func"
                placeholder="请选择采集函数"
                :options="customFuncOptions"
                option-filter-prop="label"
                show-search
                allow-clear
                :loading="funcOptionsLoading"
              />
            </a-form-item>
          </a-col>
        </a-row>
        <a-form-item
          label="自定义采集函数参数(JSON)"
          name="custom_func_kwargs"
        >
          <JsonStr v-model:value="model.custom_func_kwargs" />
        </a-form-item>
      </template>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { cloneDeep } from 'lodash';
import { message } from 'ant-design-vue';
import { computed, onMounted, reactive, ref } from 'vue';
import { event_obj } from 'liyu-pc-base/init-plugin';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { useUserStore } from 'liyu-pc-base/init-plugin';
import JsonStr from 'liyu-pc-base/components/FormRender/JsonStr/index.vue';
interface Props {
  title: string;
  record: any;
}
const props = defineProps<Props>();
const emit = defineEmits(['cancel', 'ok']);

const formRef = ref<any>(null);
const loading = ref(false);
const open = ref(true);
const form_type = ref('create');
const userStore = useUserStore();
const current_org = userStore.currentOrg;
const db_name_field_lable = computed(() => {
  return model.value.db_type === 'oracle' ? 'SID/服务名' : '数据库名称';
});
const oracle_schema = computed({
  get: () => {
    if (model.value.db_options?.schema) {
      return model.value.db_options?.schema;
    }
    return '';
  },
  set: val => {
    if (typeof model.value.db_options === 'object') {
      if (val) {
        model.value.db_options.schema = val;
      } else {
        delete model.value.db_options.schema;
      }
    } else {
      if (val) {
        model.value.db_options = { schema: val };
      }
    }
  },
});

const sourceTypeOptions = [
  { value: 'db', label: '数据库' },
  { value: 'custom', label: '自定义' },
];
const dbTypeOptions = [
  { value: 'mysql', label: 'MySQL' },
  { value: 'tsql', label: 'SQL Server' },
  { value: 'oracle', label: 'Oracle' },
];

/* 自定义采集函数下拉选项（来自后端 get_collect_funcs） */
const customFuncOptions = ref<any[]>([]);
const funcOptionsLoading = ref(false);
const loadCustomFuncOptions = async () => {
  funcOptionsLoading.value = true;
  try {
    const res = await getApiData(
      { org_id: current_org?.id },
      `${props.record.model_app}/get_collect_funcs`,
    );
    customFuncOptions.value = res || [];
  } catch (e) {
    customFuncOptions.value = [];
  } finally {
    funcOptionsLoading.value = false;
  }
};

const model = ref<any>({
  name: undefined,
  source_type: 'db',
  db_type: 'mysql',
  db_host: undefined,
  db_port: undefined,
  db_user: undefined,
  db_password: undefined,
  db_name: undefined,
  db_options: {},
  custom_func: undefined,
  custom_func_kwargs: {},
});

const rules = reactive<any>({
  name: [{ required: true, message: '请填写数据源名称' }],
  source_type: [{ required: true, message: '请选择数据源类型' }],
  db_type: [{ required: true, message: '请选择数据库类型' }],
  db_host: [{ required: true, message: '请填写数据库主机' }],
  db_port: [{ required: true, message: '请填写数据库端口' }],
  db_user: [{ required: true, message: '请填写数据库用户' }],
  db_name: [{ required: true, message: '请填写数据库名称' }],
  custom_func: [{ required: true, message: '请填写自定义采集函数名称' }],
});

/* 切换数据源类型时，清空与另一类型无关的不必要校验残留 */
const onSourceTypeChange = () => {
  if (model.value.source_type === 'db') {
    model.value.custom_func = undefined;
    model.value.custom_func_kwargs = {};
  } else {
    model.value.db_host = undefined;
    model.value.db_port = undefined;
    model.value.db_user = undefined;
    model.value.db_password = undefined;
    model.value.db_name = undefined;
    model.value.db_options = {};
  }
};

/* 将 JSON 字符串解析为对象；空字符串返回 {}；解析失败抛出错误 */
const parseJsonField = (str: string, fieldName: string): any => {
  if (str === undefined || str === null || String(str).trim() === '') return {};
  try {
    return JSON.parse(str);
  } catch (e) {
    throw new Error(`${fieldName} 不是合法 JSON: ${(e as Error).message}`);
  }
};

/* 兼容 getApiData 可能的返回结构（直接对象 或 {data:{...}}） */
const extractData = (res: any) => {
  if (res && typeof res === 'object' && !Array.isArray(res) && res.data !== undefined)
    return res.data;
  return res;
};

/* 编辑：调用公共项目提供的详情接口（<app_flag>/get_<model_name>，即 get_basemodel）
   获取完整的 SyncDataSource 记录。不依赖列表行数据 props.record，因为它可能缺失
   部分字段。get_basemodel 返回单条 dict（非列表），按 id 查询（参数名
   为 <model_name>_id，即 sync_data_source_id）。 */
const loadDetail = async (id: any) => {
  const res = await getApiData(
    { sync_data_source_id: id, org_id: current_org?.id },
    `${props.record.model_app}/get_sync_data_source`,
  );
  return extractData(res) || {};
};

/* 构建提交数据：始终 cloneDeep(model.value)，兜底默认值 */
const buildPayload = () => {
  const payload: any = cloneDeep(model.value);
  payload.update_field_list = [
    'name',
    'source_type',
    'db_type',
    'db_host',
    'db_port',
    'db_user',
    'db_password',
    'db_name',
    'db_options',
    'custom_func',
    'custom_func_kwargs',
  ];
  const isDb = payload.source_type === 'db';

  // 仅保留当前类型相关字段，避免把另一类型的空值带给后端
  if (isDb) {
    delete payload.custom_func;
    delete payload.custom_func_kwargs;
    payload.custom_func_kwargs = {};
    // 编辑时密码留空则不修改：删除字段值，并从 update_field_list 中剔除，避免后端更新
    if (!payload.db_password) {
      delete payload.db_password;
      if (form_type.value === 'edit') {
        payload.update_field_list = payload.update_field_list.filter(
          (k: string) => k !== 'db_password',
        );
      }
    }
  } else {
    ['db_type', 'db_host', 'db_port', 'db_user', 'db_password', 'db_name', 'db_options'].forEach(
      k => delete payload[k],
    );
    payload.db_options = {};
    payload.custom_func_kwargs = parseJsonField(payload.custom_func_kwargs, '自定义采集函数参数');
    delete payload.custom_func_kwargs;
  }

  return payload;
};

/* 提交：URL 遵循 /{app_flag}/create_{model} 与 /{app_flag}/update_{model} */
const handleOk = async () => {
  try {
    await formRef.value.validate();
    const payload = buildPayload();
    payload.sync_data_source_id = props.record.id;
    const url =
      form_type.value === 'edit'
        ? `${props.record.model_app}/update_sync_data_source`
        : `${props.record.model_app}/create_sync_data_source`;
    if (form_type.value === 'edit') payload.id = props.record.id;
    loading.value = true;
    await getApiData({ ...payload, org_id: current_org?.id }, url);
    message.success('操作成功');
    event_obj()?.emit('refresh');
    emit('ok');
  } catch (error) {
    // 校验失败或接口异常：保持弹窗打开
  } finally {
    loading.value = false;
  }
};
const handleCancel = () => emit('cancel');

/* 初始化编辑：先调详情接口拿完整记录，再填充；并把 JSON 字段格式化为字符串 */
const initForEdit = async () => {
  const id = props.record?.id;
  // 编辑时依赖详情接口而非列表行数据（列表行可能缺失字段）
  const record = (id != null ? await loadDetail(id) : props.record) || {};
  model.value = {
    name: record.name || '',
    source_type: record.source_type || 'db',
    db_type: record.db_type || 'mysql',
    db_host: record.db_host || undefined,
    db_port: record.db_port ?? undefined,
    db_user: record.db_user || undefined,
    db_password: undefined, // 密码不回显，留空表示不修改
    db_name: record.db_name || undefined,
    db_options: record.db_options,
    custom_func: record.custom_func || undefined,
    custom_func_kwargs: record.custom_func_kwargs,
  };
  // 编辑时若已保存的采集函数不在当前选项列表中，补充进下拉避免丢失显示
  const rawCustomFunc = record.custom_func;
  if (rawCustomFunc && !customFuncOptions.value.some(o => o.value === rawCustomFunc)) {
    customFuncOptions.value = [
      { value: rawCustomFunc, label: rawCustomFunc },
      ...customFuncOptions.value,
    ];
  }
};

onMounted(async () => {
  if (props.record?.form_type) form_type.value = props.record.form_type;
  await loadCustomFuncOptions();
  if (form_type.value === 'edit') await initForEdit();
});
</script>

<style scoped lang="less">
.field-extra {
  color: #999;
  font-size: 12px;
  line-height: 1.4;
  margin-top: 2px;
}
</style>
